import type { TFunction } from 'i18next';
import { REDACTED_RFC } from '@subscriptions/domain/types';

/**
 * One backend invoicing failure translated into copy the user can act on.
 *
 * `pattern` is matched against a single fragment of the raw message; `values` pulls the parts
 * worth quoting back out of that match — plus `taxId`, which the caller supplies rather than the
 * message, since the RFC is redacted before the text ever gets here.
 */
type InvoiceErrorRule = {
  /** Key suffix under `invoiceData:invoiceDataForm.retryErrors` */
  id: string;
  pattern: RegExp;
  values?: (match: RegExpMatchArray, taxId: string) => Record<string, string>;
};

/** Separator the backend uses to pack several validation failures into one message. */
const FRAGMENT_SEPARATOR = ';';

/**
 * Ordered because the first match wins, and some raw messages overlap: the
 * `DomicilioFiscalReceptor` one also ends in "en la lista de RFC inscritos no cancelados en el
 * SAT", so it has to be tested before the plain not-registered-RFC rule or it would be
 * reported as a bad RFC instead of a bad postal code.
 */
const INVOICE_ERROR_RULES: InvoiceErrorRule[] = [
  { id: 'postalCodeMismatch', pattern: /DomicilioFiscalReceptor/i },
  { id: 'nameMismatch', pattern: /El campo Nombre del receptor/i },
  { id: 'rfcNotRegistered', pattern: /no existe en la lista de RFC inscritos/i },
  { id: 'postalCodeLength', pattern: /c[óo]digo postal debe contener 5/i },
  {
    // The RFC is deliberately not captured: it arrives redacted, and the screen showing this
    // already knows which fiscal record it is looking at.
    id: 'regimeNotForPerson',
    pattern: /r[ée]gimen\s+(\d+)\s+no aplica para persona/i,
    values: (match, taxId) => ({ regime: match[1], rfc: taxId }),
  },
  {
    id: 'cfdiUseInvalid',
    pattern: /uso del cfdi\s+(\w+)\s+no existe/i,
    values: (match) => ({ code: match[1] }),
  },
  {
    id: 'amountsInvalid',
    pattern: /precio unitario|Subtotal|c[áa]lculo del (subtotal|total)|campo Total|campo Base/i,
  },
  { id: 'missingSubscriptionAnchor', pattern: /no subscription_uuid anchor/i },
];

/**
 * Turns a raw invoicing failure from the backend into user-facing copy.
 *
 * The backend reports these in SAT/PAC vocabulary ("El campo DomicilioFiscalReceptor del
 * receptor, debe encontrarse en la lista de RFC inscritos no cancelados en el SAT."), which
 * says nothing to the person looking at the form. Each known failure is mapped to a sentence
 * naming the field to fix instead.
 *
 * A single response can pack several failures separated by `;` — the amount-validation ones
 * always arrive that way — so the message is split first and every fragment resolved on its own.
 * Fragments that collapse onto the same rule are reported once.
 *
 * Anything unrecognized falls through as its original text: an untranslated message the user can
 * forward to support beats swallowing a failure nobody anticipated.
 *
 * Adding a failure is a two-step change: a rule here and its
 * `invoiceDataForm.retryErrors.<id>` entry in the locale file.
 *
 * @param rawMessage - Message as the backend sent it (possibly `;`-separated, possibly null)
 * @param t - i18next translation function bound to the `invoiceData` namespace
 * @param taxId - RFC of the record being shown, re-inserted into the copy that names it. Comes
 * from the caller because the message arrives with the RFC redacted (Charter §5.1).
 * @returns User-facing messages, in the order the backend reported them, without duplicates
 *
 * @example
 * resolveInvoiceErrorMessages('El regimen 601 no aplica para persona el rfc: XAXX010101000', t);
 * // ['El régimen fiscal 601 no aplica para el RFC XAXX010101000. …']
 */
export const resolveInvoiceErrorMessages = (
  rawMessage: string | null | undefined,
  t: TFunction,
  taxId?: string | null,
): string[] => {
  const trimmed = rawMessage?.trim();

  if (!trimmed) return [];

  const resolved = trimmed
    .split(FRAGMENT_SEPARATOR)
    .map((fragment) => fragment.trim())
    .filter(Boolean)
    .map((fragment) => {
      for (const rule of INVOICE_ERROR_RULES) {
        const match = fragment.match(rule.pattern);
        if (match) return t(`invoiceDataForm.retryErrors.${rule.id}`, rule.values?.(match, taxId ?? REDACTED_RFC));
      }

      return fragment;
    });

  return [...new Set(resolved)];
};
